import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, FileText, Users, Building2, Loader2, Inbox, Search, Handshake, GraduationCap, Store, ClipboardList } from 'lucide-react';

interface Company {
  id: string;
  name: string;
  verified: boolean;
  company_type: string | null;
}

export default function CompanyDashboard() {
  const { t } = useTranslation();
  const { user, profile, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [company, setCompany] = useState<Company | null>(null);
  const [opportunityCount, setOpportunityCount] = useState(0);
  const [applicationCount, setApplicationCount] = useState(0);
  const [bidCount, setBidCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && (!user || role !== 'company')) {
      navigate('/auth');
      return;
    }

    if (user) {
      fetchCompanyData();
    }
  }, [user, role, authLoading, navigate]);

  const fetchCompanyData = async () => {
    if (!user) return;

    try {
      // Fetch company
      const { data: companyData } = await supabase
        .from('companies')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (companyData) {
        setCompany(companyData);

        // Fetch opportunity count
        const { count: oppCount } = await supabase
          .from('opportunities')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', companyData.id);

        setOpportunityCount(oppCount || 0);

        // Fetch opportunities to get application count
        const { data: opps } = await supabase
          .from('opportunities')
          .select('id')
          .eq('company_id', companyData.id);

        if (opps && opps.length > 0) {
          const oppIds = opps.map(o => o.id);
          const { count: appCount } = await supabase
            .from('applications')
            .select('*', { count: 'exact', head: true })
            .in('opportunity_id', oppIds);
          setApplicationCount(appCount || 0);

          // Bid count for investors (received bids)
          if (companyData.company_type !== 'congolese_sme') {
            const { count: bCount } = await supabase
              .from('bids')
              .select('*', { count: 'exact', head: true })
              .in('opportunity_id', oppIds);
            setBidCount(bCount || 0);
          }
        }

        // Bid count for SMEs (submitted bids)
        if (companyData.company_type === 'congolese_sme') {
          const { count: bCount } = await supabase
            .from('bids')
            .select('*', { count: 'exact', head: true })
            .eq('company_id', companyData.id);
          setBidCount(bCount || 0);
        }
      }
    } catch (error) {
      console.error('Error fetching company data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="container flex min-h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  // If no company profile exists, redirect to profile setup
  if (!company) {
    return (
      <Layout>
        <div className="container py-12">
          <Card className="mx-auto max-w-lg">
            <CardHeader>
              <CardTitle>{t('company.profile.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-muted-foreground">
                {t('company.dashboard.setupPrompt')}
              </p>
              <Button asChild>
                <Link to="/profile/company/setup">
                  {t('company.dashboard.setupButton')}
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold">
            {t('company.dashboard.welcome')}, {company.name}
          </h1>
          {!company.verified && (
            <p className="mt-2 text-sm text-warning">
              ⏳ Your company is pending verification by an administrator.
            </p>
          )}
        </div>

        {/* Action Tiles - tailored by company type */}
        {(() => {
          const isSme = company.company_type === 'congolese_sme';
          
          const smeTiles = [
            { icon: Search, label: t('forBusinesses.cta.findOpportunities'), path: '/opportunities?type=tender,jv,service_offer', color: 'bg-primary/10 text-primary' },
            { icon: Store, label: t('opportunities.types.service_offer'), path: '/opportunities/create', color: 'bg-secondary/10 text-secondary' },
            { icon: Handshake, label: t('forCompanies.cta.findPartners'), path: '/companies?type=us_company', color: 'bg-accent/20 text-accent-foreground' },
            { icon: ClipboardList, label: t('bids.myBids', 'My Bids'), path: '/dashboard/company/bids', color: 'bg-primary/10 text-primary', count: bidCount },
            { icon: GraduationCap, label: t('forBusinesses.programs.title'), path: '/programs', color: 'bg-muted text-muted-foreground' },
          ];

          const intlTiles = [
            { icon: Plus, label: t('company.dashboard.postOpportunity'), path: '/opportunities/create', color: 'bg-primary/10 text-primary' },
            { icon: FileText, label: t('company.dashboard.myOpportunities'), path: '/dashboard/company/opportunities', color: 'bg-secondary/10 text-secondary', count: opportunityCount },
            { icon: Inbox, label: 'Applications Inbox', path: '/dashboard/company/applications', color: 'bg-primary/10 text-primary', count: applicationCount },
            { icon: ClipboardList, label: t('bids.receivedBids', 'Received Bids'), path: '/dashboard/company/bids', color: 'bg-secondary/10 text-secondary', count: bidCount },
            { icon: Users, label: isSme ? t('forCompanies.cta.findPartners') : t('company.dashboard.browseTalent'), path: isSme ? '/companies?type=us_company' : '/talent', color: 'bg-accent/20 text-accent-foreground' },
          ];

          const tiles = isSme ? smeTiles : intlTiles;

          return (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
              {tiles.map((tile) => (
                <Card key={tile.path + tile.label} className="cursor-pointer transition-shadow hover:shadow-lg" onClick={() => navigate(tile.path)}>
                  <CardContent className="flex flex-col items-center p-6 text-center">
                    <div className={`mb-4 flex h-14 w-14 items-center justify-center rounded-full ${tile.color}`}>
                      <tile.icon className="h-7 w-7" />
                    </div>
                    <h3 className="font-semibold">{tile.label}</h3>
                    {'count' in tile && typeof (tile as any).count === 'number' && (
                      <p className="mt-1 text-2xl font-bold text-primary">{(tile as any).count}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          );
        })()}

        {/* Recent Activity */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-lg">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {applicationCount > 0 ? (
              <p className="text-muted-foreground">
                You have <strong>{applicationCount}</strong> application{applicationCount !== 1 ? 's' : ''} across your opportunities.{' '}
                <Link to="/dashboard/company/applications" className="text-primary hover:underline">
                  View all →
                </Link>
              </p>
            ) : (
              <p className="text-muted-foreground">
                Your latest opportunities and applicants will appear here.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
